import React, { useEffect, useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { fetchAcknowledgements } from "services/posts.service";
import { PostAcknowledgements } from "types/Post";
import { formatDateAndTime, useUiLanguage } from "../utils/newsDates";
import { CompanyPill } from "./UserNameWithCompany";

interface AcknowledgementsDialogProps {
  companyId: string;
  postId: string;
  /** A post for the whole group is read by people of several companies, so each name carries its company. */
  showCompany: boolean;
  onClose: () => void;
}

/** Who has pressed "Susipažinau" on a pinned post and who has not yet. Mounted only while open; it loads the lists then. */
export function AcknowledgementsDialog({ companyId, postId, showCompany, onClose }: AcknowledgementsDialogProps) {
  const { t } = useTranslation();
  const language = useUiLanguage();
  const [lists, setLists] = useState<PostAcknowledgements | null>(null);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    let isStillWanted = true;
    fetchAcknowledgements(companyId, postId)
      .then((answer) => {
        if (isStillWanted) setLists(answer);
      })
      .catch((err) => {
        console.error("Error loading acknowledgements:", err);
        if (isStillWanted) setHasFailed(true);
      });
    return () => {
      isStillWanted = false;
    };
  }, [companyId, postId]);

  const companyOf = (person: { companyName: string | null }) => (showCompany ? person.companyName : null);

  const renderLists = () => {
    if (hasFailed) return <p className="mt-4 text-sm text-red-600">{t("news_ack_load_failed")}</p>;
    if (!lists) return <div className="mt-4 h-16 animate-pulse rounded bg-gray-200" />;
    return (
      <>
        <PeopleList
          title={t("news_ack_pending_heading", { number: lists.pending.length })}
          emptyText={t("news_ack_nobody_pending")}
          people={lists.pending.map((person) => ({ id: person.userId, name: person.name, companyName: companyOf(person) }))}
        />
        <PeopleList
          title={t("news_ack_acknowledged_heading", { number: lists.acknowledged.length })}
          emptyText={t("news_ack_nobody_acknowledged")}
          people={lists.acknowledged.map((person) => ({
            id: person.userId,
            name: person.name,
            companyName: companyOf(person),
            at: formatDateAndTime(person.at, language),
          }))}
        />
      </>
    );
  };

  return (
    <Dialog open onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/30" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="max-h-[80dvh] w-full max-w-md overflow-y-auto rounded-lg bg-white p-5 shadow-xl">
          <DialogTitle className="text-lg font-semibold text-gray-900">{t("news_ack_dialog_title")}</DialogTitle>
          {renderLists()}
          <div className="mt-5 flex justify-end">
            <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 font-medium text-gray-600 hover:bg-gray-100">
              {t("feed_close")}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

interface ListedPerson {
  id: string;
  name: string;
  /** Null when the companies are not shown. */
  companyName: string | null;
  /** When the person acknowledged, already formatted; only the acknowledged list has it. */
  at?: string;
}

/** A titled list of people (name, their company when shown, when they acknowledged), or `emptyText` when there is nobody. */
function PeopleList({ title, people, emptyText }: { title: string; people: ListedPerson[]; emptyText: string }) {
  return (
    <>
      <h3 className="mt-4 text-sm font-semibold text-gray-700">{title}</h3>
      {people.length === 0 ? (
        <p className="py-1 text-sm text-gray-500">{emptyText}</p>
      ) : (
        <ul>
          {people.map((person) => (
            <li key={person.id} className="flex items-center justify-between gap-3 py-1 text-sm text-gray-900">
              <span className="min-w-0 break-words">
                {person.name}
                {person.companyName && <CompanyPill name={person.companyName} className="ml-2 align-middle" />}
              </span>
              {person.at && <span className="flex-shrink-0 text-gray-500">{person.at}</span>}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
